import { supabase } from '../lib/supabase';

/**
 * Fetch user management analytics using the admin RPC function
 * which bypasses RLS and returns accurate counts for all users.
 */
export const getUserManagementAnalytics = async () => {
  try {
    // Use the admin_get_all_users function with a large page size to get all users
    // We fetch page 1 with a large limit to get total_count from the first row
    const { data: rows, error: rpcError } = await supabase?.rpc('admin_get_all_users', {
      p_page: 1,
      p_page_size: 1000
    });

    if (rpcError) {
      console.error('Error fetching analytics via admin function:', rpcError);
      throw rpcError;
    }

    const totalUsers = rows?.[0]?.total_count ? Number(rows?.[0]?.total_count) : (rows?.length || 0);

    // Active users: last_activity_date within last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
    const thirtyDaysAgoStr = thirtyDaysAgo?.toISOString()?.split('T')?.[0];

    const activeUsers = rows?.filter(u => {
      if (!u?.last_activity_date) return false;
      return u?.last_activity_date >= thirtyDaysAgoStr;
    })?.length || 0;

    // New users this month: created_at >= start of current month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
    const newUsersThisMonth = rows?.filter(u => {
      if (!u?.created_at) return false;
      return u?.created_at >= startOfMonth;
    })?.length || 0;

    // Average engagement: average total_verses_memorized across all users
    // (a simple proxy for engagement that doesn't require extra queries)
    let averageEngagement = 0;
    if (rows?.length > 0) {
      const totalVerses = rows?.reduce((sum, u) => sum + (u?.total_verses_memorized || 0), 0);
      averageEngagement = parseFloat((totalVerses / rows?.length)?.toFixed(1));
    }

    return {
      totalUsers,
      activeUsers,
      newUsersThisMonth,
      averageEngagement
    };
  } catch (error) {
    console.error('Failed to fetch user management analytics:', error);
    return {
      totalUsers: 0,
      activeUsers: 0,
      newUsersThisMonth: 0,
      averageEngagement: 0
    };
  }
};

/**
 * Fetch user growth trend data (last 6 months)
 * @returns {Promise<Array>} Array of objects with month and user count
 */
export const getUserGrowthTrend = async () => {
  try {
    // Use admin function to get all users with their creation dates
    const { data: rows, error } = await supabase?.rpc('admin_get_all_users', {
      p_page: 1,
      p_page_size: 1000
    });

    if (error) {
      console.error('Error fetching user growth data:', error);
      throw error;
    }

    if (!rows || rows?.length === 0) {
      return [];
    }

    // Get the last 6 months
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now?.getFullYear(), now?.getMonth() - i, 1);
      months?.push({
        month: date?.toLocaleDateString('en-US', { month: 'short' }),
        year: date?.getFullYear(),
        monthIndex: date?.getMonth(),
        users: 0
      });
    }

    // Count cumulative users up to each month
    months?.forEach((monthData) => {
      const monthEndDate = new Date(monthData?.year, monthData?.monthIndex + 1, 0, 23, 59, 59);
      const cumulativeCount = rows?.filter(user => {
        const userDate = new Date(user?.created_at);
        return userDate <= monthEndDate;
      })?.length;
      monthData.users = cumulativeCount;
    });

    return months?.map(m => ({ month: m?.month, users: m?.users }));
  } catch (error) {
    console.error('Failed to fetch user growth trend:', error);
    return [];
  }
};

/**
 * Calculate engagement for a user (interactions per month)
 */
const calculateUserEngagement = async (userId, userCreatedAt) => {
  try {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const { count: reviewsThisMonth } = await supabase?.from('review_history')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.gte('reviewed_at', startOfMonth?.toISOString());
    const { count: reflectionsThisMonth } = await supabase?.from('verse_reflections')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.gte('created_at', startOfMonth?.toISOString());
    const { count: versesThisMonth } = await supabase?.from('verses')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.gte('created_at', startOfMonth?.toISOString());

    const thisMonthTotal = (reviewsThisMonth || 0) + (reflectionsThisMonth || 0) + (versesThisMonth || 0);

    const { count: totalReviews } = await supabase?.from('review_history')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId);
    const { count: totalReflections } = await supabase?.from('verse_reflections')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId);
    const { count: totalVerses } = await supabase?.from('verses')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId);

    const totalInteractions = (totalReviews || 0) + (totalReflections || 0) + (totalVerses || 0);
    const createdDate = new Date(userCreatedAt);
    const monthsSinceJoined = Math.max(1,
      (now?.getFullYear() - createdDate?.getFullYear()) * 12 +
      (now?.getMonth() - createdDate?.getMonth()) + 1
    );
    const averagePerMonth = Math.round(totalInteractions / monthsSinceJoined);

    return { thisMonth: thisMonthTotal, average: averagePerMonth };
  } catch (error) {
    console.error('Error calculating engagement:', error);
    return { thisMonth: 0, average: 0 };
  }
};

/**
 * Format relative time
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSeconds < 60) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
};

/**
 * Fetch user table data with pagination
 */
export const getUserTableData = async (page = 1, pageSize = 50) => {
  try {
    const { data: rows, error: rpcError } = await supabase?.rpc('admin_get_all_users', {
      p_page: page,
      p_page_size: pageSize
    });

    if (rpcError) {
      console.error('Error fetching users via admin function:', rpcError);
      throw rpcError;
    }

    if (!rows || rows?.length === 0) {
      return {
        users: [],
        pagination: { page, pageSize, totalUsers: 0, totalPages: 0 }
      };
    }

    const totalCount = rows?.[0]?.total_count ? Number(rows?.[0]?.total_count) : 0;

    // Fetch live verse counts for all users in one query
    const userIds = rows?.map(u => u?.id);

    const { data: verseCounts } = await supabase?.from('verses')?.select('user_id')?.in('user_id', userIds)?.neq('phase', 0);

    const verseCountMap = {};
    verseCounts?.forEach(v => {
      verseCountMap[v.user_id] = (verseCountMap?.[v?.user_id] || 0) + 1;
    });

    // Fetch live streak data for all users
    const { data: streakData } = await supabase?.from('user_streaks')?.select('user_id, current_streak')?.in('user_id', userIds);

    const streakMap = {};
    streakData?.forEach(s => {
      streakMap[s.user_id] = s?.current_streak || 0;
    });

    const usersWithEngagement = await Promise.all(
      rows?.map(async (user) => {
        const engagement = await calculateUserEngagement(user?.id, user?.created_at);
        const daysSinceActivity = user?.last_activity_date
          ? Math.floor((new Date() - new Date(user.last_activity_date)) / (1000 * 60 * 60 * 24))
          : 999;
        const status = daysSinceActivity <= 45 ? 'active' : 'inactive';

        return {
          id: user?.id,
          username: user?.username || 'N/A',
          email: user?.email,
          status,
          role: user?.role || 'user',
          isActive: user?.is_active ?? true,
          joinDate: new Date(user.created_at)?.toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
          }),
          lastActive: formatRelativeTime(user?.last_activity_date),
          versesMemorized: verseCountMap?.[user?.id] || user?.total_verses_memorized || 0,
          streak: streakMap?.[user?.id] ?? user?.streak_count ?? 0,
          engagement: {
            thisMonth: engagement?.thisMonth,
            average: engagement?.average
          }
        };
      })
    );

    return {
      users: usersWithEngagement,
      pagination: {
        page,
        pageSize,
        totalUsers: totalCount,
        totalPages: Math.ceil(totalCount / pageSize)
      }
    };
  } catch (error) {
    console.error('Failed to fetch user table data:', error);
    return {
      users: [],
      pagination: { page: 1, pageSize, totalUsers: 0, totalPages: 0 }
    };
  }
};

/**
 * Toggle user account enabled/disabled status
 */
export const toggleUserAccountStatus = async (userId, isActive) => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({ is_active: isActive })
      ?.eq('id', userId)
      ?.select('id, username, email, is_active')
      ?.single();

    if (error) {
      console.error('Error toggling user account status:', error);
      throw error;
    }

    return {
      success: true,
      user: data,
      message: `User account ${isActive ? 'enabled' : 'disabled'} successfully`
    };
  } catch (error) {
    console.error('Failed to toggle user account status:', error);
    return {
      success: false,
      error: error?.message || 'Failed to update account status'
    };
  }
};

/**
 * Fetch role distribution and permissions data from the database
 */
export const getRolePermissionsData = async () => {
  try {
    const { data: rows, error } = await supabase?.rpc('admin_get_all_users', {
      p_page: 1,
      p_page_size: 1000
    });

    if (error) {
      console.error('Error fetching role data:', error);
      throw error;
    }

    const roleCounts = {};
    rows?.forEach(row => {
      const role = row?.role || 'user';
      roleCounts[role] = (roleCounts?.[role] || 0) + 1;
    });

    const permissionMatrix = {
      admin: { canImport: true, canExport: true, canModerate: true, canAccessAdmin: true },
      beta_tester: { canImport: true, canExport: true, canModerate: true, canAccessAdmin: false },
      premium: { canImport: true, canExport: true, canModerate: false, canAccessAdmin: false },
      standard: { canImport: false, canExport: true, canModerate: false, canAccessAdmin: false },
      user: { canImport: false, canExport: true, canModerate: false, canAccessAdmin: false },
    };

    const roleLabels = {
      admin: 'Admin',
      beta_tester: 'Beta Tester',
      premium: 'Premium',
      standard: 'Standard',
      user: 'Standard',
    };

    const result = [];
    let idCounter = 1;

    const standardCount = (roleCounts?.['standard'] || 0) + (roleCounts?.['user'] || 0);
    if (standardCount > 0) {
      result?.push({ id: idCounter++, role: 'Standard', users: standardCount, ...permissionMatrix?.['standard'] });
    }

    ['premium', 'beta_tester', 'admin']?.forEach(role => {
      if (roleCounts?.[role] > 0) {
        result?.push({ id: idCounter++, role: roleLabels?.[role], users: roleCounts?.[role], ...permissionMatrix?.[role] });
      }
    });

    if (result?.length === 0) {
      return [
        { id: 1, role: 'Standard', users: 0, canImport: false, canExport: true, canModerate: false, canAccessAdmin: false },
        { id: 2, role: 'Premium', users: 0, canImport: true, canExport: true, canModerate: false, canAccessAdmin: false },
        { id: 3, role: 'Beta Tester', users: 0, canImport: true, canExport: true, canModerate: true, canAccessAdmin: false },
        { id: 4, role: 'Admin', users: 0, canImport: true, canExport: true, canModerate: true, canAccessAdmin: true },
      ];
    }

    return result;
  } catch (error) {
    console.error('Failed to fetch role permissions data:', error);
    return [
      { id: 1, role: 'Standard', users: 0, canImport: false, canExport: true, canModerate: false, canAccessAdmin: false },
      { id: 2, role: 'Premium', users: 0, canImport: true, canExport: true, canModerate: false, canAccessAdmin: false },
      { id: 3, role: 'Beta Tester', users: 0, canImport: true, canExport: true, canModerate: true, canAccessAdmin: false },
      { id: 4, role: 'Admin', users: 0, canImport: true, canExport: true, canModerate: true, canAccessAdmin: true },
    ];
  }
};

/**
 * Fetch community management statistics from the database
 */
export const getCommunityStats = async () => {
  try {
    const { count: totalReflections, error: reflError } = await supabase
      ?.from('verse_reflections')
      ?.select('*', { count: 'exact', head: true });

    if (reflError) console.error('Error fetching reflections count:', reflError);

    const { count: publicProfiles, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('*', { count: 'exact', head: true })
      ?.eq('reflection_visibility', 'public');

    if (profileError) console.error('Error fetching public profiles:', profileError);

    let flaggedContent = 0;
    try {
      const { count: flagged, error: flagError } = await supabase
        ?.from('verse_reflections')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('is_flagged', true);
      if (!flagError) flaggedContent = flagged || 0;
    } catch (e) {
      flaggedContent = 0;
    }

    const startOfMonth = new Date();
    startOfMonth?.setDate(1);
    startOfMonth?.setHours(0, 0, 0, 0);

    let moderationActions = 0;
    try {
      const { count: modCount, error: modError } = await supabase
        ?.from('verse_reflections')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('is_public', false)
        ?.gte('updated_at', startOfMonth?.toISOString());
      if (!modError) moderationActions = modCount || 0;
    } catch (e) {
      moderationActions = 0;
    }

    return {
      totalReflections: totalReflections || 0,
      publicProfiles: publicProfiles || 0,
      flaggedContent,
      moderationActions,
    };
  } catch (error) {
    console.error('Failed to fetch community stats:', error);
    return { totalReflections: 0, publicProfiles: 0, flaggedContent: 0, moderationActions: 0 };
  }
};

/**
 * Send password reset email to a specific user
 */
export const sendPasswordResetEmail = async (email) => {
  try {
    const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
      redirectTo: `${window.location?.origin}/reset-password`
    });
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error sending password reset:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Export all user data as CSV
 */
export const exportUserDataAsCSV = async () => {
  try {
    const { data: rows, error } = await supabase?.rpc('admin_get_all_users', {
      p_page: 1,
      p_page_size: 1000
    });
    if (error) throw error;

    const headers = ['Username', 'Email', 'Role', 'Active', 'Streak', 'Verses Memorized', 'Last Activity', 'Joined'];
    const csvRows = [headers?.join(',')];

    rows?.forEach(u => {
      const row = [
        `"${u?.username || ''}"`,
        `"${u?.email || ''}"`,
        `"${u?.role || ''}"`,
        u?.is_active ? 'Yes' : 'No',
        u?.streak_count || 0,
        u?.total_verses_memorized || 0,
        `"${u?.last_activity_date || ''}"`,
        `"${u?.created_at ? new Date(u.created_at)?.toLocaleDateString() : ''}"`,
      ];
      csvRows?.push(row?.join(','));
    });

    const csvContent = csvRows?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, count: rows?.length || 0 };
  } catch (error) {
    console.error('Error exporting user data:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Delete a user account permanently via edge function (uses service role)
 * Deletes auth user + all associated data
 */
export const deleteUserAccount = async (userId) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('delete-user', {
      body: { userId },
    });

    if (error) {
      console.error('Error invoking delete-user function:', error);
      return { success: false, error: error?.message || 'Failed to delete user account' };
    }

    if (data?.error) {
      return { success: false, error: data?.error };
    }

    return { success: true, message: data?.message || 'User account deleted successfully' };
  } catch (error) {
    console.error('Failed to delete user account:', error);
    return { success: false, error: error?.message || 'Failed to delete user account' };
  }
};
